import { Component, inject, linkedSignal, signal, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ExpansionPanelV2Component } from '@app/framework/expansion-panel-v2/expansion-panel-v2.component';
import { OptionsListSimpleComponent } from '@app/framework/overlays/options-list-simple/options-list-simple.component';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { OptionsAction } from '@app/framework/overlays/option-list.constants';
import { INTERACTION_BAR_STATES } from '@app/framework/constants/interaction-bar.constants';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { colors } from 'src/colors';
import { InvitationsStore } from '@app/store/invitations/invitations.store';
import { PageLoadingComponent } from '@app/framework/page-loading/page-loading.component';
import { SplashScreenComponent } from '@app/framework/splash-screen/splash-screen.component';
import { LoadingBarComponent } from '@app/framework/inputs/loading-bar/loading-bar.component';
import {
  DisplayInvitation,
  DisplayInvitationGroup,
  INVITATION_STATUS,
} from '@app/store/invitations/invitations.types';
import { NotificationV2Service } from '@app/notification-v2.service';
import { CurrentUserService } from '@app/services/current-user.service';
import { MessagesStateService } from '@app/services/messages-state.service';

@Component({
  selector: 'app-invitations-listing',
  imports: [
    ExpansionPanelV2Component,
    OptionsListSimpleComponent,
    SimpleButtonComponent,
    SplashScreenComponent,
    PageLoadingComponent,
    LoadingBarComponent,
    FormsModule,
    NgClass,
    NgStyle,
  ],
  templateUrl: './invitations-listing.component.html',
  styleUrl: './invitations-listing.component.scss',
})
export class InvitationsListingComponent implements OnInit, OnDestroy {
  isDestroyed$ = new Subject<boolean>();

  invitationsStore = inject(InvitationsStore);
  interactionBarState = inject(InteractionBarStateService);
  notif = inject(NotificationV2Service);
  userService = inject(CurrentUserService);
  messagesStateService = inject(MessagesStateService);

  invitationGroupOptions = signal<OptionsAction[] | null>([{ value: 'rename', label: 'Rename' }]);

  loading = this.invitationsStore.isLoading;
  accessibleUsers = toSignal(this.userService.accessibleUsers$);

  INVITATION_STATUS_COLORS: Record<INVITATION_STATUS, string> = {
    [INVITATION_STATUS.ACCEPTED]: colors['green-400'],
    [INVITATION_STATUS.DISMISSED]: colors['purple-300'],
    [INVITATION_STATUS.PENDING]: colors['blue-700'],
    [INVITATION_STATUS.INVALID]: colors['red-200'],
    [INVITATION_STATUS.CANCELLED]: colors['navy-600'],
  };

  searchText = this.invitationsStore.searchText;
  invitationGroups = this.invitationsStore.invitationGroups;

  /**
   * Map of groupId to whether the panel is expanded <br/>
   * If there is search text, all panels are expanded because the user wants to see search results <br/>
   * If there is no search text, it keeps the previous state (so the user doesn't get annoyed) <br/>
   * This is a linkedSignal and it is recomputed when either searchText or invitationGroups change.
   */
  isExpanded = linkedSignal<
    {
      searchText: string | null;
      invitationGroups: DisplayInvitationGroup[];
    },
    Record<number, boolean>
  >({
    source: () => ({
      searchText: this.searchText(),
      invitationGroups: this.invitationGroups(),
    }),
    computation: (sources, previousValue) => {
      const map = {};
      if (!sources.searchText) {
        sources.invitationGroups.forEach((g) => {
          map[g.id] = previousValue?.value?.[g.id] ?? false;
        });
        return map;
      }

      sources.invitationGroups.forEach((g) => {
        map[g.id] = !!sources.searchText;
      });
      return map;
    },
  });

  ngOnInit() {
    this.invitationsStore.loadInvitationsSent();
  }

  togglePanelExpansion(groupId: number) {
    const current = this.isExpanded()[groupId];
    this.isExpanded.update((prev) => ({ ...prev, [groupId]: !current }));
  }

  openContactsSidebar() {
    this.interactionBarState.openInteractionBar(INTERACTION_BAR_STATES.CONTACT_SIDEBAR);
  }

  openInvitationsSidebar() {
    this.interactionBarState.openInteractionBar(INTERACTION_BAR_STATES.INVITES_SIDEBAR);
  }

  invitationOptions(invitation: DisplayInvitation | null): OptionsAction[] {
    if (!invitation) return [];

    const options: OptionsAction[] = [];
    if (invitation.status !== 'accepted') {
      if (
        invitation.status !== INVITATION_STATUS.DISMISSED &&
        invitation.status !== INVITATION_STATUS.CANCELLED
      ) {
        options.push({ value: 'cancel', label: 'Cancel Invitation' });
      }
      if (invitation.status !== INVITATION_STATUS.PENDING) {
        options.push({ value: 'resend', label: 'Resend Invitation' });
      }
    }
    return options;
  }
  onInvitationOptionSelected(option: OptionsAction, invitation: DisplayInvitation | null) {
    if (!invitation) return;

    switch (option.value) {
      case 'resend':
        if (invitation.isPrime) {
          this.invitationsStore.resendPrimeInvite(invitation.id);
        } else {
          this.invitationsStore.resendSubcontractInvite(invitation.id);
        }
        break;
      case 'cancel': {
        this.notif
          .showConfirmDialog(
            `Are you sure you want to cancel ${invitation.name ?? 'this invitation'}?`,
          )
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((response) => {
            if (response) {
              if (invitation.isPrime) {
                this.invitationsStore.cancelSentPrimeInvitation(invitation.id);
              } else {
                this.invitationsStore.cancelSentSubcontractInvitation(invitation.id);
              }
            }
          });
        break;
      }
    }
  }

  sendMessageTo(invitation: DisplayInvitation | null) {
    if (!invitation || !invitation.email) {
      this.notif.showError('Cannot send message to this invitation. Email not found.');
      return;
    }

    const user = this.accessibleUsers()?.find((user) => user.email === invitation.email);
    if (!user) {
      this.notif.showError('Cannot send message to this contact. User not found in our platform.');
      return;
    }

    this.messagesStateService.newMessageTo(user);
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
